export type UTM = Partial<{
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_campaign_id: string;
  utm_campaign_name: string;
  utm_term: string;
  utm_content: string;
}>;

// Exported so the inline hydration script in layout/Head reads the same cookie
// under the same name instead of keeping its own copy of the string.
export const UTM_COOKIE = '__gtm_campaign_url';
export const UTM_SESSION_KEY = 'session_utm_payload';
const LS_UTM = UTM_SESSION_KEY;

const MAX_UTM_FIELD_LEN = 200;

const truncate = (val: string | undefined, max: number = MAX_UTM_FIELD_LEN): string | undefined => {
  if (!val) {
    return undefined;
  }

  return val.length > max ? val.slice(0, max) : val;
};

export const readCookieRaw = (name: string): string | null => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return m ? decodeURIComponent(m[1]) : null;
};

const isPrismaticHost = (): boolean =>
  location.hostname.endsWith('.prismatic.io') || location.hostname === 'prismatic.io';

export const writeCookie = (name: string, value: string, days = 90): void => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const domain = isPrismaticHost() ? '; domain=.prismatic.io' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/${domain}; SameSite=Lax`;
};

const pickFirst = (...vals: Array<string | null | undefined>): string | undefined => {
  const v = vals.find(x => typeof x === 'string' && x.trim().length > 0);

  return v?.trim() || undefined;
};

const normalizeCampaignName = (val?: string): string | undefined => {
  if (!val) {
    return undefined;
  }

  const cleaned = val.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim();

  return cleaned || undefined;
};

export const parseUtmFromUrl = (url: string): UTM => {
  const u = new URL(url, location.origin);
  const get = (k: string) => truncate(u.searchParams.get(k) || undefined);

  const utm_campaign_id = pickFirst(get('utm_campaign'));

  const campaignNameRaw = pickFirst(get('utm_campaign_name'), get('utm_campaignname'), get('utm_campaignName'));

  const utm_campaign_name = truncate(normalizeCampaignName(campaignNameRaw));

  const utm_campaign = pickFirst(utm_campaign_name, utm_campaign_id);

  const base: UTM = {
    utm_source: get('utm_source'),
    utm_medium: get('utm_medium'),
    utm_campaign,
    utm_campaign_id,
    utm_campaign_name,
    utm_term: get('utm_term'),
    utm_content: get('utm_content'),
  };

  const hasAny = Boolean(
    base.utm_source ||
    base.utm_medium ||
    base.utm_campaign ||
    base.utm_campaign_name ||
    base.utm_campaign_id ||
    base.utm_term ||
    base.utm_content,
  );

  return hasAny ? base : {};
};

type CompactUtm = {
  s?: string;
  m?: string;
  c?: string;
  ci?: string;
  cn?: string;
  t?: string;
  co?: string;
};

const toCompactUtm = (utm: UTM): CompactUtm => ({
  s: utm.utm_source,
  m: utm.utm_medium,
  c: utm.utm_campaign,
  ci: utm.utm_campaign_id,
  cn: utm.utm_campaign_name,
  t: utm.utm_term,
  co: utm.utm_content,
});

const fromCompactUtm = (c: CompactUtm): UTM => ({
  utm_source: c.s,
  utm_medium: c.m,
  utm_campaign: c.c,
  utm_campaign_id: c.ci,
  utm_campaign_name: c.cn,
  utm_term: c.t,
  utm_content: c.co,
});

export const hasUtmValues = (utm: UTM | null | undefined): boolean =>
  Boolean(utm && Object.values(utm).some(v => typeof v === 'string' && v.trim().length > 0));

const DAY_MS = 24 * 60 * 60 * 1000;

type LocalCopy<T> = { v: T; exp: number };

/**
 * Durable same-browser copy of a cookie value, for Safari.
 *
 * ITP caps every cookie written from JavaScript, and when the visitor arrives
 * from a domain it classifies as a tracker (linkedin.com, google.com) with
 * parameters on the URL, which is every paid click, the cap is 24 hours. A
 * visitor who clicks a LinkedIn ad on an iPhone and converts the next day
 * reached the form with nothing, while Dreamdata, which keeps the first touch
 * server side, still had the campaign. localStorage survives seven days under
 * the same rules, so it is the longest window available without a server-set
 * cookie (CloudFront strips Set-Cookie from origin responses).
 *
 * Only ever written next to the 90-day cookie, so it carries exactly the same
 * consent rule: an EU visitor who has not consented gets neither.
 */
export const writeLocalCopy = <T>(key: string, value: T, days: number): void => {
  try {
    const copy: LocalCopy<T> = { v: value, exp: Date.now() + days * DAY_MS };
    localStorage.setItem(key, JSON.stringify(copy));
  } catch {
    /* no-op */
  }
};

export const readLocalCopy = <T>(key: string): { value: T; daysLeft: number } | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const copy = JSON.parse(raw) as LocalCopy<T>;
    const msLeft = copy.exp - Date.now();
    if (!copy.v || !(msLeft > 0)) {
      localStorage.removeItem(key);

      return null;
    }

    return { value: copy.v, daysLeft: Math.ceil(msLeft / DAY_MS) };
  } catch {
    return null;
  }
};

const UTM_LOCAL_KEY = 'persisted_utm_payload';

export const writeUtmToCookie = (utm: UTM, days = 90): void => {
  if (!hasUtmValues(utm)) {
    return;
  }

  writeCookie(UTM_COOKIE, JSON.stringify(toCompactUtm(utm)), days);
  writeLocalCopy(UTM_LOCAL_KEY, utm, days);
};

/**
 * The durable copy, used when Safari has already expired the cookie. Rewrites
 * the cookie for the remaining lifetime, so GTM and the inline hydration script,
 * which only read the cookie, see the campaign again too.
 */
const restoreUtmFromLocal = (): UTM | null => {
  const copy = readLocalCopy<UTM>(UTM_LOCAL_KEY);
  if (!copy || !hasUtmValues(copy.value)) {
    return null;
  }

  writeCookie(UTM_COOKIE, JSON.stringify(toCompactUtm(copy.value)), copy.daysLeft);

  return copy.value;
};

/**
 * Returns null, never an empty object, when the cookie holds no campaign.
 *
 * `readEffectiveUTM` picks the first truthy of cookie-then-session, and `{}` is
 * truthy: an empty result here silently swallowed the sessionStorage copy and
 * sent the lead to Marketo with no campaign at all. `__gtm_campaign_url` is a
 * shared name from the GTM era, so a tag rewriting it with a campaign-less URL
 * is exactly how that happens mid-session.
 */
export const readUtmFromCookie = (): UTM | null => {
  const raw = readCookieRaw(UTM_COOKIE);
  if (!raw) {
    return null;
  }

  if (raw.startsWith('{')) {
    try {
      const parsed = fromCompactUtm(JSON.parse(raw) as CompactUtm);

      return hasUtmValues(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  try {
    const parsed = parseUtmFromUrl(raw);
    if (!hasUtmValues(parsed)) {
      return null;
    }

    // Legacy format: the cookie used to hold the raw landing URL. Rewrite it in
    // the compact shape so it is read consistently from here on.
    writeUtmToCookie(parsed);

    return parsed;
  } catch {
    return null;
  }
};

export const writeUtmToSession = (utm: UTM): void => {
  try {
    sessionStorage.setItem(LS_UTM, JSON.stringify(utm));
  } catch {
    /* no-op */
  }
};

export const readUtmFromSession = (): UTM | null => {
  try {
    const raw = sessionStorage.getItem(LS_UTM);
    const parsed = raw ? (JSON.parse(raw) as UTM) : null;

    return hasUtmValues(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const readEffectiveUTM = (): UTM | null =>
  readUtmFromCookie() || restoreUtmFromLocal() || readUtmFromSession();

const UTM_PARAM_KEYS: Array<keyof UTM> = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_campaign_id',
  'utm_campaign_name',
  'utm_term',
  'utm_content',
];

/**
 * Returns `target` with the visitor's UTM parameters carried over, taken from
 * the current URL and falling back to the persisted attribution cookie/session.
 *
 * A post-submit redirect to a thank-you page would otherwise land on a clean URL,
 * so any tracking that fires there (Wistia conversion pixel, Marketo Munchkin, GTM)
 * reads no UTMs and attributes the lead to Direct. Params already present on
 * `target` win, so an explicitly configured value is never overwritten.
 */
export const appendUtmToUrl = (target: string): string => {
  try {
    const url = new URL(target, location.origin);
    const current = new URLSearchParams(location.search);
    const fallback = (readEffectiveUTM() || {}) as Record<string, string | undefined>;

    UTM_PARAM_KEYS.forEach(key => {
      if (url.searchParams.has(key)) {
        return;
      }

      const value = current.get(key) || fallback[key];
      if (value) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  } catch {
    return target;
  }
};

export const captureUtmByRegion = ({
  isEU,
  hasConsent,
  cookieDays = 90,
}: {
  isEU: boolean;
  hasConsent: boolean;
  cookieDays?: number;
}): void => {
  const utm = parseUtmFromUrl(location.href);
  if (!Object.keys(utm).length) {
    return;
  }

  // The session copy is written for everyone, not only for the EU visitor who
  // has not consented yet. It is the same-tab backup for the cookie: nothing is
  // stored that the visitor is not already storing (a consenting visitor gets
  // the 90-day cookie too), and a third-party tag that overwrites
  // `__gtm_campaign_url` can no longer take the campaign down with it.
  writeUtmToSession(utm);

  if (!isEU || hasConsent) {
    writeUtmToCookie(utm, cookieDays);
  }
};

export const promoteSessionUtmToCookie = (days = 90): void => {
  const utm = readUtmFromSession();
  if (utm) {
    writeUtmToCookie(utm, days);
  }
};
